import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Notice } from '../page/Notice';
import type { SupplierPick } from './api';

/** «Padova, 14 usi»: where the pick is and how much it is used. */
export function useWhere() {
  const { t } = useTranslation('suppliers');
  return (pick: SupplierPick): string => {
    const uses = t('picker.uses', { count: pick.uses });
    return pick.city ? t('picker.duplicate.where', { city: pick.city, uses }) : uses;
  };
}

/**
 * Board 1.6: the VAT number is already another supplier's. Creation stops; the existing one can
 * be used as it is, get the typed name as a site, or as another name (§3.2.4).
 */
export function DuplicateNotice({
  existing,
  typedName,
  busy,
  failed,
  onUse,
  onAddSite,
  onAddName,
}: {
  readonly existing: SupplierPick;
  readonly typedName: string;
  readonly busy: boolean;
  readonly failed: boolean;
  readonly onUse: () => void;
  readonly onAddSite: () => void;
  readonly onAddName: () => void;
}) {
  const { t } = useTranslation('suppliers');
  const where = useWhere();
  const name = existing.legalName ?? existing.name;
  const differs = typedName.trim() !== '' && typedName.trim() !== existing.name;
  return (
    <div role="alert">
      <Notice
        tone="warning"
        icon={TriangleAlert}
        title={t('picker.duplicate.title', { name })}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={busy}
              onClick={onUse}
            >
              {t('picker.duplicate.use', { name })}
            </Button>
            {differs && (
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                disabled={busy}
                onClick={onAddSite}
              >
                {t('picker.duplicate.addSite', { site: typedName.trim() })}
              </Button>
            )}
            {differs && (
              <Button
                type="button"
                variant="ghost"
                className="min-h-11"
                disabled={busy}
                onClick={onAddName}
              >
                {t('picker.duplicate.addName')}
              </Button>
            )}
          </>
        }
      >
        <span>
          {where(existing)}. {t('picker.duplicate.text')}
        </span>
        {failed && <span className="text-destructive">{t('picker.duplicate.failed')}</span>}
      </Notice>
    </div>
  );
}
